import { get, isEmpty, sortBy, find } from 'lodash-es';
import dateFormat from 'dateformat';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { BRANDS, ALL_BRANDS, GENERIC_DISCLAIMER } from 'site-modules/multi-offer/constants/brands';
import { MULTI_OFFER_RESULTS, OFFER_DETAILS } from 'site-modules/multi-offer/constants/render-types';
import { REDEMPTION_STATES } from 'site-modules/multi-offer/constants/redemption-states';
import { CTA_TEXTS } from 'site-modules/multi-offer/components/multi-offer-results/offer-details-card/constants';
import { getAddressLine2 } from 'site-modules/shared/utils/appraisal/common-utils';
import { toDisplayString } from 'site-modules/shared/utils/phone-format';
import { getPriceRangeString } from 'site-modules/shared/utils/price-utils';
import { isExpiredOffer } from 'site-modules/shared/utils/multi-offer/offers';

/**
 * Given the offer details vehicle object, it will return the year make model to be displayed.
 * @param {Object} vehicle
 * @returns {String} If empty, it will return empty string.
 */
export const getYearMakeModelDisplay = (vehicle = {}) => {
  if (isEmpty(vehicle)) return '';
  const { year, make, model } = vehicle;

  return `${year} ${make} ${model}`;
};

/**
 * Given the offer details vehicle object, it will return the make model to be displayed.
 * @param {Object} vehicle
 * @returns {String} If empty, it will return empty string.
 */
export const getMakeModelDisplay = (vehicle = {}) => {
  if (isEmpty(vehicle)) return '';
  const { make, model } = vehicle;

  return `${make} ${model}`;
};

/**
 * Given the offer details object, it will return the mileage to be displayed.
 * @param {Object} vehicle
 * @param {String} append - Text to append
 * @returns {String}
 */
export const getMileageDisplay = (vehicle = {}, append = '') => {
  if (isEmpty(vehicle)) return '';
  const { mileage } = vehicle;

  return `${numberWithCommas(mileage)}${append}`;
};

/**
 * Given the offer details object, it will return the estimate to be displayed.
 * @param {Object} offerDetails
 * @returns {String}
 */
export const getEstimateDisplay = offerDetails =>
  getPriceRangeString(offerDetails?.estimate?.category?.['below-average'], offerDetails?.estimate?.category?.excellent);

const EXPIRES_DISPLAY_DATE_MASK = 'm/d/yy';
/**
 * Given the offer details object, it will return the formatted expired date to be displayed.
 * @param {Object} offerDetails
 *    @param {String} expiresDisplayDate
 * @param {String} mask optional parameter to specify the date format mask, default is {@link EXPIRES_DISPLAY_DATE_MASK}.
 * @returns {String}
 */
export const getExpiresDisplayDate = ({ expiresDisplayDate }, mask = EXPIRES_DISPLAY_DATE_MASK) => {
  try {
    const isUTCFormat = new Date(expiresDisplayDate).getUTCHours() === 0;
    return dateFormat(expiresDisplayDate, mask, isUTCFormat);
  } catch {
    // we should NOT expect a value that can be parsed as a date from the "expiresDisplayDate" field.
    return expiresDisplayDate;
  }
};

/**
 * Determines if the brand exist in ALL_BRANDS, if not it will be consider as "generic".
 * @param {Object} offerDetails
 *    @param {String} brand
 * @returns {Boolean}
 */
export const isGenericBrand = ({ brand }) => brand === BRANDS.GENERIC || !ALL_BRANDS.has(brand);

export const isCarmaxOffer = offer => offer?.brand === BRANDS.KMX;

export const isEstimatedOffer = offerDetails => {
  const estimate = get(offerDetails, 'estimate');
  const valuation = get(offerDetails, 'valuation');

  return !valuation && !!estimate;
};

/**
 * Returns a NEW array with offers sorted according to the 'offerDisplayNumber' field.
 *
 * @param {Array} offers offers to display on the page; this array is NOT modified.
 * @returns {Array} offers in the order for displaying on the page.
 */
export const sortOffersForDisplay = offers => sortBy(offers, ['offerDisplayNumber']);

/**
 * Returns brand name from brand assets, if it doesn't exist then it will return it's "brand" because it's consider "generic".
 *
 * If the offer is obscured (see EMO-1257), then it returns the "Brand 2"/"Brand 3" string, unless the isForPixel param
 * is specified as "true".
 *
 * IMPORTANT NOTE: if you use this function for tracking/reporting, then most likely you need to pass the isForPixel
 * parameter as "true".
 *
 * @param {Object} offerDetails
 *    @param {String} brand
 *    @param {Boolean} isObscured
 *    @param {Number} offerDisplayNumber
 *    @param {String} dealerName
 * @param {Boolean} isForPixel flag, that indicates whether the returned brand name will be used for a pixel value.
 * If the passed offerDetails denotes an obscured offer, then when this flag is "false" a placeholder (like "Brand 2")
 * is returned instead of the real brand. Optional, default is "false".
 * @returns {String}
 */
export const getBrandName = (offerDetails, isForPixel = false) => {
  const { brand, isObscured, offerDisplayNumber, dealerName, brandAssets = {} } = offerDetails;

  if (isObscured && !isForPixel) {
    return `Brand ${offerDisplayNumber}`;
  }

  if (brand === BRANDS.ICO && isForPixel) {
    // Currently, for ICO we use 'ICO' text for pixel values, NOT dealer name.
    return 'ICO';
  }

  if (brand === BRANDS.ICOMODULE) {
    // Offer with BRANDS.ICOMODULE brand doesn't have brand assets, because it comes from ICODS rather than from MODS.
    return dealerName;
  }

  return brandAssets.brandName || brand;
};

const findBrandDisclaimer = (offerDetails, isWithAsteriskForEstimate) => {
  const isExpired = isExpiredOffer(offerDetails);
  const isEstimated = isEstimatedOffer(offerDetails);

  const { brand, brandAssets = {}, dealerName } = offerDetails;

  let disclaimer;

  if (brand === BRANDS.ICOMODULE) {
    // Offer with BRANDS.ICOMODULE brand doesn't have brand assets, because it comes from ICODS rather than from MODS.
    disclaimer = `The offer from ${dealerName} is contingent on your providing accurate information on Edmunds.com. ${dealerName} will conduct an in-person verification of your vehicle and evaluate other vehicle use and history information prior to finalizing the offer. Any differences between the information you provide about your vehicle and the vehicle's actual condition, use, and history may affect the offer you receive from our partners.`;
  } else {
    disclaimer = brandAssets.disclaimer || GENERIC_DISCLAIMER;
  }

  if (isEstimated && !isExpired && isWithAsteriskForEstimate) {
    // Adding an asterisk so that the displayed disclaimer has double asterisk.
    return `*${disclaimer}`;
  }

  return disclaimer;
};

/**
 * Returns disclaimer from brand assets (with fallback to GENERIC_DISCLAIMER).
 * @param {Object} offerDetails
 *    @param {String} brand
 *    @param {Boolean} isObscured
 * @returns {String}
 */
export const getBrandDisclaimer = (offerDetails, isWithAsteriskForEstimate = true) => {
  const disclaimer = findBrandDisclaimer(offerDetails, isWithAsteriskForEstimate);
  if (typeof disclaimer === 'function') {
    return disclaimer(offerDetails);
  }
  return disclaimer;
};

/**
 * Returns expired offer copy.
 * @param {Object} offerDetails
 *    @param {String} expiresDisplayDate
 * @returns {Object}
 */
export const getExpiredOfferCopy = offerDetails => ({
  header: "It's time to refresh your offer",
  listItems: [
    `This offer expired on ${getExpiresDisplayDate(offerDetails)}`,
    'Refresh your offer to get the updated value',
  ],
});

export const getExpiredEstimatedOfferCopy = offerDetails => ({
  header: 'It’s time to refresh your car’s value',
  listItems: [
    `This estimated offer is out of date as of ${getExpiresDisplayDate(offerDetails)}`,
    'Refresh to get an updated offer or estimate',
  ],
});

/**
 * Determines whether it is an ODP (Offer Details Page) EXPIRED render case.
 * @param {String} renderType
 * @returns {Boolean}
 */
export const isOdpExpiredOffer = renderType => OFFER_DETAILS.EXPIRED === renderType;

export const isOdpEstimatedOffer = renderType => OFFER_DETAILS.ESTIMATED === renderType;

export const isOdpEstimatedExpiredOffer = renderType => OFFER_DETAILS.ESTIMATED_EXPIRED === renderType;

export const isOdpAnyTypeEstimatedOffer = renderType =>
  isOdpEstimatedOffer(renderType) || isOdpEstimatedExpiredOffer(renderType);

/**
 * Determines whether it is an ODP (Offer Details Page) render case, where the Appointment View is displayed (i.e. the
 * user is encouraged to visit CarMax).
 * @param {String} renderType
 * @returns {Boolean}
 */
export const isOdpAppointment = renderType => [OFFER_DETAILS.NO_ESTIMATE, OFFER_DETAILS.NO_OFFER].includes(renderType);

export const isOdpNoPartners = renderType => OFFER_DETAILS.NO_PARTNERS === renderType;

/**
 * Determines whether it is an ODP (Offer Details Page) render case, where we have no accepted offer, no estimate.
 * @param {String} renderType
 * @returns {Boolean}
 */
export const isOdpNoOfferNoEstimate = renderType => isOdpAppointment(renderType) || isOdpNoPartners(renderType);

/**
 * Determines whether it is pending offers.
 * @param {String} renderType
 * @returns {Boolean}
 */
export const isPendingOffers = renderType => MULTI_OFFER_RESULTS.PENDING === renderType;

/**
 * Determines whether SOME but NOT all offers have expired on MOR page.
 * @param {String} renderType
 * @returns {Boolean}
 */
export const isSomeOffersExpired = renderType => MULTI_OFFER_RESULTS.DEFAULT_HAS_EXPIRED === renderType;

/**
 * Determines whether ALL offers have expired on MOR page.
 * @param {String} renderType
 * @returns {Boolean}
 */
export const isAllOffersExpired = renderType => MULTI_OFFER_RESULTS.DEFAULT_ALL_EXPIRED === renderType;

/**
 * Determines if an expired offer is present on MOR page by checking renderType.
 *
 * @param {String} renderType
 * @returns {Boolean} true if renderType is MULTI_OFFER_RESULTS.DEFAULT_ALL_EXPIRED or MULTI_OFFER_RESULTS.DEFAULT_HAS_EXPIRED
 */
export const hasAnyExpiredOffer = renderType => isSomeOffersExpired(renderType) || isAllOffersExpired(renderType);

/**
 * Determines whether to show the "refresh offer" button by checking renderType
 * @param {String} renderType
 * @returns {Boolean}
 */
export const isShowRefreshOffersButton = renderType => isAllOffersExpired(renderType);

export const isPostPiiRedemptionFinalized = redemptionState =>
  redemptionState === REDEMPTION_STATES.POST_PII_REDEMPTION_FINALIZED;

export const isPostPiiRedemptionPending = redemptionState =>
  redemptionState === REDEMPTION_STATES.POST_PII_REDEMPTION_PENDING;

export const isHighestOffer = ({ offerNumber }) => offerNumber === 1;

/**
 * Returns offer with the highest valuation.
 *
 * @param {Array} offers array with offers.
 * @returns {Object} offer object.
 */
export const getHighestOffer = offers => find(offers, isHighestOffer) || get(offers, 0, {});

/**
 * Returns a positive valuation difference between the highest and second offers.
 *
 * @param {Array} offers
 * @returns {Number|null} valuation difference or null when unavailable/non-positive.
 */
export const getHighestOfferDiff = offers => {
  const sortedOffers = sortOffersForDisplay(offers);
  const highestOffer = sortedOffers?.[0];
  const secondOffer = sortedOffers?.[1];

  if (!highestOffer || !secondOffer) {
    return null;
  }

  const diff = highestOffer.valuation - secondOffer.valuation;
  return diff > 0 ? diff : null;
};

export const getBrandOffer = (offers, brand, partner) =>
  partner ? find(offers, { brand, partner }) : find(offers, { brand });

/**
 * Returns active (non-expired) offers.
 *
 * @param {Array} offers
 * @returns {Array}
 */
export const getNonExpiredOffers = offers => offers.filter(offer => !isExpiredOffer(offer));

// In-store offers are CarMax offers that have an active in-store appraisal
export const getIsKmxInStoreOffer = offer => offer.brand === BRANDS.KMX && offer.verification?.status === 'active';

/**
 * Determines whether Edmunds CarMax disclaimer should be displayed.
 *
 * @param {Array} offers offer list to inspect.
 * @returns {Boolean}
 */
export const shouldDisplayEdmundsCarmaxDisclaimer = (offers = []) => {
  const carmaxOffer = offers.find(offer => offer?.brand === BRANDS.KMX);

  if (!carmaxOffer || carmaxOffer.isObscured) {
    return false;
  }

  return !isExpiredOffer(carmaxOffer);
};

/**
 * Returns true when offer code should be shown in UI.
 *
 * @param {Object} offer offer details object.
 * @returns {Boolean}
 */
function shouldDisplayOfferCode(offer) {
  const isKmxOffer = offer?.brand === BRANDS.KMX;
  const isIcoOffer = offer?.brand === BRANDS.ICO;

  if (isKmxOffer) {
    return !getIsKmxInStoreOffer(offer);
  }

  return isIcoOffer;
}

/**
 * Returns offer code to display in UI.
 *
 * @param {String} code offer code.
 * @param {String} brand offer brand.
 * @returns {String} offer code to display in UI.
 */
export const getDisplayOfferCode = offer => (shouldDisplayOfferCode(offer) ? offer?.code : '');

/**
 * Given the offer details object, it will return the string ordinal number of this offer, using the 'offerNumber' field.
 *
 * @param {Object} offerDetails
 *    @param {Number} offerNumber
 * @returns {String}
 */
export const getOfferOrdinalNumber = ({ offerNumber }) => {
  switch (offerNumber) {
    case 1:
      return '1st';
    case 2:
      return '2nd';
    case 3:
      return '3rd';
    default:
      // Assume, that theoretically there can be more than 3 offers, but not more than 20.
      return `${offerNumber}th`;
  }
};

/**
 * Placeholder text that is used for obscured offers.
 *
 * @param {Object} offerDetails offer object.
 * @returns {String} placeholder text.
 */
export const getOfferPlaceholder = offerDetails => `${getOfferOrdinalNumber(offerDetails)} Offer`;

export const getOfferProvider = offerDetails => {
  const { isObscured } = offerDetails;
  return isObscured ? 'the offer provider' : getBrandName(offerDetails);
};

export const canScheduleAppt = (offerDetails, hasCarmaxStore) => {
  const { brand } = offerDetails;
  // we cannot schedule an appointment with CarMax if we don't have a CarMax store.
  return brand === BRANDS.KMX && hasCarmaxStore;
};

/**
 * Returns if the given offers are the same.
 *
 * @param {Object} offerDetails1 offer 1.
 * @param {Object} offerDetails2 offer 2.
 * @returns {Boolean}
 */
export const areOffersEqual = (offerDetails1, offerDetails2) =>
  ['partner', 'source', 'brand'].every(key => get(offerDetails1, key) === get(offerDetails2, key));

/**
 * Determines whether or not the text-shadow-contrast-high class should be applied
 * based on the `isTextContrastRequired` field from the brand assets object of the offer object.
 * @param {Object} offerDetails offer object
 * @returns {Boolean}
 */
export const isContrastRequired = offerDetails => {
  const { brand, brandAssets } = offerDetails;
  if (brand === BRANDS.ICOMODULE) {
    return true;
  }
  return !!brandAssets?.colors?.isTextContrastRequired;
};

export const COLOR_ID = {
  PRIMARY: 'PRIMARY',
  SECONDARY: 'SECONDARY',
  TEXT: 'TEXT',
};

const buildColorStyles = ({ primaryColor, secondaryColor, textColor }, colorIds) => {
  let targetIds = colorIds;
  if (isEmpty(targetIds)) {
    // Choose all by default.
    targetIds = Object.values(COLOR_ID);
  }

  const styles = {};

  if (primaryColor && targetIds.includes(COLOR_ID.PRIMARY)) {
    styles['--brand-asset-primary'] = primaryColor;
  }
  if (secondaryColor && targetIds.includes(COLOR_ID.SECONDARY)) {
    styles['--brand-asset-secondary'] = secondaryColor;
  }
  if (textColor && targetIds.includes(COLOR_ID.TEXT)) {
    styles['--brand-asset-text'] = textColor;
  }

  return isEmpty(styles) ? undefined : styles;
};

/**
 * Builds a CSS custom properties object from offer's brandAssets.colors for partner-specific theming.
 * @param {Object} offerDetails offer object.
 * @param {Array<String>} colorIds ids of the colors that should be returned from this function (see {@link COLOR_ID}).
 * This field is optional, all colors are returned by default.
 * @returns {Object|undefined} style object with CSS custom properties, or undefined if no colors are present.
 */
export const getBrandColors = (offerDetails, ...colorIds) => {
  const { brand, brandAssets } = offerDetails;

  if (brand === BRANDS.ICOMODULE) {
    return buildColorStyles(
      {
        primaryColor: '#1358bf',
        secondaryColor: '#2070e8',
        textColor: '#ffffff',
      },
      colorIds
    );
  }

  return buildColorStyles(brandAssets?.colors || {}, colorIds);
};

export const getCardBtnDefaultText = (offer, isEmo4345Chal1 = false) => {
  const { redemptionState } = offer;

  if (isEmo4345Chal1 && isCarmaxOffer(offer)) {
    if (isEstimatedOffer(offer) || isPostPiiRedemptionFinalized(redemptionState)) {
      return CTA_TEXTS.SCHEDULE_APPOINTMENT_EMO4345;
    }
    return CTA_TEXTS.CONTINUE_EMO4345;
  }

  if (isEstimatedOffer(offer)) {
    return CTA_TEXTS.SCHEDULE_APPOINTMENT;
  }

  if (isPostPiiRedemptionFinalized(redemptionState)) {
    return CTA_TEXTS.CONTINUE;
  }

  return CTA_TEXTS.VIEW_OFFER_DETAILS;
};

export const isActiveFirmOffer = renderType => OFFER_DETAILS.DEFAULT === renderType;

export const parseStoreRawData = (rawStoreData = '{}', formatPhone = true, brand) => {
  const parsedStoreData = JSON.parse(rawStoreData);
  const { city, state, zipCode, addressLine2 } = parsedStoreData;

  if (brand === BRANDS.KMX && !addressLine2) {
    parsedStoreData.addressLine2 = getAddressLine2({ city, state, zipCode });
  }

  if (!formatPhone || !parsedStoreData?.phoneNumber || typeof parsedStoreData.phoneNumber !== 'object') {
    return parsedStoreData;
  }

  const formattedPhoneNumber = toDisplayString(parsedStoreData.phoneNumber, 'plain');
  return {
    ...parsedStoreData,
    phoneNumber: formattedPhoneNumber,
  };
};

export const parseNearestStoresRawData = (rawStoresData = '[]', brand) => {
  const parsedStoresData = JSON.parse(rawStoresData);

  if (brand === BRANDS.KMX) {
    return parsedStoresData.map(store => {
      const { city, state, zipCode, addressLine2 } = store;

      if (!addressLine2) {
        return {
          ...store,
          addressLine2: getAddressLine2({ city, state, zipCode }),
        };
      }

      return store;
    });
  }

  return parsedStoresData;
};

export const isHighestOfferUISuppressed = ({ offerDetails, renderType }) => {
  if (offerDetails.length !== 2 || hasAnyExpiredOffer(renderType)) {
    // NOTE: if any offer is expired, then the "Highest Offer" UI is NOT displayed anyway.
    return false;
  }
  const estimatedOffer = offerDetails.find(isEstimatedOffer);
  if (!estimatedOffer) {
    return false;
  }
  const otherOffer = offerDetails.find(offer => offer !== estimatedOffer);
  const otherOfferValuation = otherOffer?.valuation;
  if (!otherOfferValuation) {
    return false;
  }
  const estimateLowest = estimatedOffer.estimate?.category?.['below-average'];
  return !!estimateLowest && (estimateLowest - otherOfferValuation) / estimateLowest >= 0.3;
};

export const hasHighestOfferBadge = ({ individualOfferDetails, offerDetails, renderType }) => {
  return (
    isHighestOffer(individualOfferDetails) &&
    offerDetails.length > 1 &&
    !hasAnyExpiredOffer(renderType) &&
    !isHighestOfferUISuppressed({ offerDetails, renderType })
  );
};

export const isAppraisalsOfferSource = offersResults => offersResults.source === 'appraisals';
